import {useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {createCurrencyRate, updateCurrencyRate, deleteCurrencyRate} from '../../api/currencyRate';
import {selectCurrencyRates, addCurrencyRate, updateCurrencyRateById, removeCurrencyRateById} from '../../store/currencyRates';
import {findAllAccounts} from '../../api/capitalAccount';
import {findAllProjects} from '../../api/project';
import {findAllCompanies} from '../../api/company';
import {setAccounts} from '../../store/accounts';
import {setProjects} from '../../store/projects';
import {setCompanies} from '../../store/companies';
import Modal from '../../components/common/Modal';
import {formatNumber} from '../../utils';
import './CurrencyRatePage.scss';

const syncRelated = (dispatch) =>
    Promise.all([findAllAccounts(), findAllProjects(), findAllCompanies()])
        .then(([accounts, projects, companies]) => {
            dispatch(setAccounts(accounts));
            dispatch(setProjects(projects));
            dispatch(setCompanies(companies));
        });

const emptyForm = () => ({
    date: '',
    uahToUsdRate: '',
    uahToEurRate: '',
});

const rateToForm = (r) => ({
    date: r.date?.slice(0, 10) ?? '',
    uahToUsdRate: String(r.uahToUsdRate ?? ''),
    uahToEurRate: String(r.uahToEurRate ?? ''),
});

const formToPayload = (f) => ({
    date: f.date || null,
    uahToUsdRate: Number(f.uahToUsdRate),
    uahToEurRate: Number(f.uahToEurRate),
});

const CurrencyRatePage = () => {
    const dispatch = useDispatch();
    const rawRates = useSelector(selectCurrencyRates);
    const rates = useMemo(
        () => [...rawRates].sort((a, b) => new Date(b.date) - new Date(a.date)),
        [rawRates],
    );

    const [form, setForm] = useState(emptyForm());
    const [editId, setEditId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [showForm, setShowForm] = useState(false);

    const openCreate = () => {
        setForm(emptyForm());
        setEditId(null);
        setShowForm(true);
    };

    const openEdit = (r) => {
        setForm(rateToForm(r));
        setEditId(r.id);
        setShowForm(true);
    };

    const cancel = () => {
        setShowForm(false);
        setEditId(null);
        setForm(emptyForm());
    };

    const save = () => {
        setSaving(true);
        const payload = formToPayload(form);
        const req = editId ? updateCurrencyRate(editId, payload) : createCurrencyRate(payload);
        req.then(saved => {
            cancel();
            if (editId) dispatch(updateCurrencyRateById(saved));
            else dispatch(addCurrencyRate(saved));
            syncRelated(dispatch);
        }).finally(() => setSaving(false));
    };

    const remove = (id) => {
        if (!window.confirm('Delete this currency rate?')) return;
        deleteCurrencyRate(id).then(() => {
            dispatch(removeCurrencyRateById(id));
            syncRelated(dispatch);
        });
    };

    const canSubmit = form.date && form.uahToUsdRate !== '' && form.uahToEurRate !== '';

    return (
        <div className="capital-page">
            <div className="capital-page__header">
                <h2 className="capital-page__title">Currency Rates</h2>
                <button className="wallet-add-button" onClick={openCreate}>
                    + Rate
                </button>
            </div>

            {showForm && (
                <Modal onClose={cancel}>
                    <form className="currency-form"
                          onSubmit={(e) => { e.preventDefault(); if (canSubmit) save(); }}>
                        <h3 className="currency-form__title">{editId ? 'Edit Rate' : 'Add Rate'}</h3>

                        <div className="currency-form__field">
                            <label className="currency-form__label">Date</label>
                            <input className="currency-form__input" type="date" value={form.date}
                                   onChange={e => setForm(f => ({...f, date: e.target.value}))} required/>
                        </div>

                        <div className="currency-form__row">
                            <div className="currency-form__field">
                                <label className="currency-form__label">UAH → USD Rate</label>
                                <input className="currency-form__input" type="number" step="0.0001"
                                       placeholder="41.5000" value={form.uahToUsdRate}
                                       onChange={e => setForm(f => ({...f, uahToUsdRate: e.target.value}))} required/>
                            </div>
                            <div className="currency-form__field">
                                <label className="currency-form__label">UAH → EUR Rate</label>
                                <input className="currency-form__input" type="number" step="0.0001"
                                       placeholder="45.5000" value={form.uahToEurRate}
                                       onChange={e => setForm(f => ({...f, uahToEurRate: e.target.value}))} required/>
                            </div>
                        </div>

                        <div className="currency-form__actions">
                            <button type="button" className="currency-form__btn" onClick={cancel}>Cancel</button>
                            <button type="submit" className="currency-form__btn currency-form__btn--primary"
                                    disabled={!canSubmit || saving}>
                                {saving ? 'Saving…' : editId ? 'Save' : 'Add'}
                            </button>
                        </div>
                    </form>
                </Modal>
            )}

            <div className="scrollable-list-holder">
                {rates.length === 0 && <p className="currency-rate-empty">No currency rates configured.</p>}
                {rates.map(r => (
                    <div key={r.id} className="currency-rate-card" onClick={() => openEdit(r)}>
                        <span className="currency-rate-card__date">{r.date?.slice(0, 10)}</span>
                        <div className="currency-rate-card__values">
                            <span>USD {formatNumber(r.uahToUsdRate)}</span>
                            <span>EUR {formatNumber(r.uahToEurRate)}</span>
                        </div>
                        <button className="btn-del" title="Delete"
                                onClick={e => { e.stopPropagation(); remove(r.id); }}>
                            ×
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default CurrencyRatePage;
